import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, CircularProgress, Switch } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { siteService } from 'components/api/sites';
import { commonGridStyles } from 'styles/gridStyles';
import { showToast } from 'store/toastSlice';
import { useAppDispatch } from 'store/hooks';
import styles from './GeoTab.module.scss';

interface GeoTabProps {
  siteId: string | number;
}

interface RestrictedCountry {
  id: number;
  siteId: number;
  countryCode: string;
  countryName: string;
  isEnabled: boolean;
  isLicenceMandated: boolean;
  note: string;
  createdAt: string;
  updatedAt: string;
}

const GeoTab: React.FC<GeoTabProps> = ({ siteId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<RestrictedCountry[]>([]);
  const dispatch = useAppDispatch();

  const fetchRestrictedCountries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await siteService.getRestrictedCountries(siteId);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch restricted countries:', error);
      dispatch(showToast({ text: 'Failed to fetch restricted countries', type: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [siteId, dispatch]);

  useEffect(() => {
    fetchRestrictedCountries();
  }, [fetchRestrictedCountries]);

  const handleToggle = async (id: number, currentStatus: boolean) => {
    try {
      // Optimistic update
      setData((prev) =>
        prev.map((row) => (row.id === id ? { ...row, isEnabled: !currentStatus } : row)),
      );

      await siteService.toggleRestrictedCountry(id);
      dispatch(showToast({ text: 'Country restriction updated', type: 'success' }));
    } catch (error: any) {
      console.error('Failed to toggle restricted country:', error);
      const message = error.response?.data?.message || error.message || 'Failed to update';
      dispatch(showToast({ text: message, type: 'error' }));
      // Revert on error
      setData((prev) =>
        prev.map((row) => (row.id === id ? { ...row, isEnabled: currentStatus } : row)),
      );
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 70 },
      { field: 'countryName', headerName: 'Country Name', width: 100 },
      { field: 'note', headerName: 'Note', flex: 1, minWidth: 100 },
      {
        field: 'isEnabled',
        headerName: 'Status (Blocked)',
        width: 150,
        renderCell: (params) => (
          <Switch
            checked={params.value}
            onChange={() => handleToggle(params.row.id, params.value)}
            color="primary"
          />
        ),
      },
    ],
    [],
  );

  if (loading && data.length === 0) {
    return (
      <Box className={styles.loadingWrapper}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.gridWrapper}>
        <DataGrid
          rows={data}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          hideFooter
          sx={commonGridStyles}
        />
      </Box>
    </Box>
  );
};

export default GeoTab;
